import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { HandHeart, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 500;

const timeAgo = (iso) => {
    try {
        const t = new Date(iso).getTime();
        const diff = Math.max(0, Date.now() - t);
        const m = Math.floor(diff / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        return `${d}d ago`;
    } catch {
        return "recently";
    }
};

export const PrayerWall = () => {
    const [recent, setRecent] = useState([]);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [prayer, setPrayer] = useState("");
    const [anon, setAnon] = useState(true);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try {
            const r = await axios.get(`${API}/prayers/recent?limit=6`);
            setRecent(r.data || []);
        } catch {
            /* keep empty */
        }
    };

    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        const text = prayer.trim();
        if (!text) {
            toast.error("Share what's on your heart.");
            return;
        }
        if (text.length > MAX) {
            toast.error(`Please keep it under ${MAX} characters.`);
            return;
        }
        setLoading(true);
        try {
            const trimmedEmail = email.trim();
            await axios.post(`${API}/prayers`, {
                name: name.trim(),
                email: trimmedEmail || null,
                prayer: text,
                is_anonymous: anon || !name.trim(),
            });
            toast.success(
                trimmedEmail
                    ? "Received with love. A note is on its way to you. 💜"
                    : "Received with love. Praying with you. 💜"
            );
            setPrayer("");
            setName("");
            setEmail("");
            setAnon(true);
            load();
        } catch {
            toast.error("Couldn't send. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="prayer"
            data-testid="prayer-section"
            className="relative py-24 md:py-32 overflow-hidden"
        >
            <div className="absolute inset-0 -z-10">
                <div className="absolute top-1/4 left-1/3 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full bg-lilac-200/50 blur-3xl" />
            </div>

            <div className="mx-auto max-w-6xl px-6 md:px-10">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="eyebrow text-gold-deep flex items-center justify-center gap-2 mb-5">
                        <HandHeart className="w-3.5 h-3.5" /> The Prayer Wall
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-lilac-900 leading-tight">
                        Drop a <em className="italic gold-text">prayer</em>.
                    </h2>
                    <p className="mt-6 text-lilac-700 leading-relaxed">
                        A sacred space to lay it down. Share what's on your heart — anonymously or
                        with your name. Coach Melissa reads every one and stands with you in prayer.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-12 gap-8">
                    {/* Form */}
                    <form
                        onSubmit={submit}
                        data-testid="prayer-form"
                        className="col-span-12 lg:col-span-7 rounded-3xl bg-white border border-border p-7 md:p-9 shadow-[0_18px_50px_rgba(43,29,53,0.06)]"
                    >
                        <label className="eyebrow text-lilac-700 mb-2 block">
                            What's on your heart?
                        </label>
                        <textarea
                            value={prayer}
                            onChange={(e) => setPrayer(e.target.value.slice(0, MAX))}
                            data-testid="prayer-input-text"
                            rows={5}
                            placeholder="Lord, I'm praying for…"
                            className="w-full rounded-2xl border border-input bg-transparent px-4 py-3 text-sm md:text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                        />
                        <div className="mt-2 flex items-center justify-between text-xs text-lilac-700/70">
                            <span>Read with reverence. Held in confidence.</span>
                            <span data-testid="prayer-char-count" className="tabular-nums">
                                {prayer.length}/{MAX}
                            </span>
                        </div>

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="eyebrow text-lilac-700 mb-2 block">
                                    First name (optional)
                                </label>
                                <Input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    data-testid="prayer-input-name"
                                    placeholder="Your name"
                                    className="h-11 rounded-full px-5"
                                    disabled={anon}
                                />
                            </div>
                            <div>
                                <label className="eyebrow text-lilac-700 mb-2 block">
                                    Email (optional, for a reply)
                                </label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="prayer-input-email"
                                    placeholder="you@email.com"
                                    className="h-11 rounded-full px-5"
                                />
                            </div>
                        </div>

                        <label className="mt-3 flex items-center gap-3 h-11 px-5 rounded-full border border-border bg-lilac-50/60 cursor-pointer w-full sm:w-auto sm:inline-flex">
                            <input
                                type="checkbox"
                                checked={anon}
                                onChange={(e) => setAnon(e.target.checked)}
                                data-testid="prayer-input-anon"
                                className="accent-[hsl(var(--primary))]"
                            />
                            <span className="text-sm text-lilac-900">
                                Post anonymously to the wall
                            </span>
                        </label>

                        <div className="mt-7 flex items-center justify-between gap-4">
                            <p className="text-xs text-lilac-700/80 max-w-xs">
                                Your email is used only for Coach Melissa's reply — never shared.
                            </p>
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="prayer-submit"
                                className="rounded-full h-11 px-6 bg-gold hover:bg-gold-deep text-white"
                            >
                                {loading ? "Sending…" : "Send Prayer"} <Send className="w-4 h-4" />
                            </Button>
                        </div>
                    </form>

                    {/* Recent wall */}
                    <aside
                        data-testid="prayer-wall"
                        className="col-span-12 lg:col-span-5 rounded-3xl bg-lilac-50/70 border border-lilac-200/70 p-7 md:p-9"
                    >
                        <div className="flex items-center gap-2 eyebrow text-gold-deep mb-5">
                            <Sparkles className="w-3.5 h-3.5" /> Recently lifted
                        </div>
                        {recent.length === 0 ? (
                            <p className="text-sm text-lilac-700 leading-relaxed">
                                Be the first to drop a prayer. Your words may carry someone else home.
                            </p>
                        ) : (
                            <ul className="space-y-4 max-h-[420px] overflow-y-auto pr-2">
                                {recent.map((p) => (
                                    <li
                                        key={p.id}
                                        data-testid={`prayer-item-${p.id}`}
                                        className="rounded-2xl bg-white border border-border p-4"
                                    >
                                        <p className="font-display italic text-lilac-900 text-base leading-snug line-clamp-4">
                                            "{p.prayer}"
                                        </p>
                                        <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-wider text-lilac-700">
                                            <span>{p.display_name}</span>
                                            <span>{timeAgo(p.created_at)}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </aside>
                </div>
            </div>
        </section>
    );
};
